import React, { useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { stripePromise } from "../stripe";

interface BreakdownState {
  hostTotalCents: number;
  serviceFeeCents: number;
  grandTotalCents: number;
}

interface CheckoutState {
  clientSecret: string;
  requiresApproval: boolean;
  breakdown: BreakdownState;
}

function fmt(cents: number) {
  return `$${(cents / 100).toFixed(2)}`;
}

function CheckoutForm({
  bookingId,
  requiresApproval,
  breakdown,
}: {
  bookingId: string;
  requiresApproval: boolean;
  breakdown: BreakdownState;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/bookings/${bookingId}`,
      },
    });

    // confirmPayment redirects on success; we only reach here on error
    if (confirmError) {
      setError(confirmError.message ?? "Payment failed. Please try again.");
    }
    setSubmitting(false);
  }

  return (
    <form onSubmit={handleSubmit} style={formStyle}>
      {/* Price breakdown */}
      <div style={breakdownBox}>
        <div style={breakdownRow}>
          <span style={{ color: "#6b7280" }}>Sauna rental</span>
          <span>{fmt(breakdown.hostTotalCents)}</span>
        </div>
        <div style={breakdownRow}>
          <span style={{ color: "#6b7280" }}>Service fee</span>
          <span>{fmt(breakdown.serviceFeeCents)}</span>
        </div>
        <div style={{ ...breakdownRow, borderTop: "1px solid #e5e7eb", paddingTop: 10, marginTop: 4 }}>
          <span style={{ fontWeight: 700 }}>Total</span>
          <span style={{ fontWeight: 700 }}>{fmt(breakdown.grandTotalCents)}</span>
        </div>
      </div>

      {requiresApproval && (
        <p style={{ fontSize: 13, color: "#92400e", background: "#fef3c7", padding: "8px 12px", borderRadius: 6, margin: "0 0 16px" }}>
          This listing requires host approval. Your card will be <strong>authorized but not charged</strong> until the host approves.
        </p>
      )}

      <PaymentElement />

      {error && (
        <p style={{ fontSize: 13, color: "#dc2626", margin: "12px 0 0" }}>{error}</p>
      )}

      <button
        type="submit"
        disabled={!stripe || submitting}
        style={{ ...submitBtn, opacity: (!stripe || submitting) ? 0.6 : 1 }}
      >
        {submitting
          ? "Processing..."
          : requiresApproval
          ? `Authorize ${fmt(breakdown.grandTotalCents)}`
          : `Pay ${fmt(breakdown.grandTotalCents)}`}
      </button>

      <p style={{ fontSize: 12, color: "#9ca3af", textAlign: "center", margin: "12px 0 0" }}>
        Secure checkout powered by Stripe
      </p>
    </form>
  );
}

export default function Checkout() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { state } = useLocation() as { state: CheckoutState | null };

  if (!state?.clientSecret) {
    return (
      <div style={{ maxWidth: 480, margin: "60px auto", padding: "0 24px", textAlign: "center" }}>
        <p style={{ color: "#6b7280", marginBottom: 16 }}>
          No checkout session found. Please start your booking from the sauna listing page.
        </p>
        <Link to="/" style={{ color: "#2563EB", fontSize: 14 }}>Browse saunas</Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: "40px auto", padding: "0 24px 60px" }}>
      <Link to="/" style={{ fontSize: 13, color: "#2563EB", textDecoration: "none" }}>
        ← Back
      </Link>
      <h2 style={{ margin: "16px 0 24px", fontSize: 22, fontWeight: 700 }}>Checkout</h2>
      <Elements
        stripe={stripePromise}
        options={{ clientSecret: state.clientSecret, appearance: { theme: "stripe" } }}
      >
        <CheckoutForm
          bookingId={bookingId!}
          requiresApproval={state.requiresApproval}
          breakdown={state.breakdown}
        />
      </Elements>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const formStyle: React.CSSProperties = {
  border: "1px solid #e5e7eb", borderRadius: 10,
  padding: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
};

const breakdownBox: React.CSSProperties = {
  marginBottom: 20, padding: "14px 0",
  borderBottom: "1px solid #e5e7eb",
};

const breakdownRow: React.CSSProperties = {
  display: "flex", justifyContent: "space-between",
  fontSize: 14, marginBottom: 8,
};

const submitBtn: React.CSSProperties = {
  width: "100%", marginTop: 20,
  padding: "12px 24px", background: "#2563EB", color: "#fff",
  border: "none", borderRadius: 8, cursor: "pointer", fontSize: 15, fontWeight: 600,
};
